import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useAuthorizedMe } from '@/common/api/modules/auth'
import { useSiteGetApi, useSiteListApi } from '@/common/api/modules/site'
import { masterTypeName } from '@/common/app/modules/master'
import '@/common/app/path'
import {
  aiPurchaseAssistAbTestsPath,
  aiPurchaseAssistAiProfilesPath,
  aiPurchaseAssistCampaignsPath,
  aiPurchaseAssistConversationsPath,
  aiPurchaseAssistFeedbackPath,
  aiPurchaseAssistKnowledgePath,
  aiPurchaseAssistNgRulesPath,
  aiPurchaseAssistOpeningsPath,
  aiPurchaseAssistPath,
  aiPurchaseAssistRichUIPath,
  aiPurchaseAssistVectorSourcesPath,
  aiTagsPath,
  analysisTargetPath,
  analyticsPath,
  articleAnalysisListPath,
  articleApprovalListPath,
  articleListPath,
  articleNewPath,
  auditLogsPath,
  behaviorPath,
  billingPath,
  customersPath,
  dashboardPath,
  deliveryStatsPath,
  distributionListPath,
  externalArticlePath,
  featureListPath,
  fileUploadPath,
  linkageApiKeyListPath,
  masterSummaryPath,
  mediaActionPath,
  mediaAiTrainingDataPath,
  mediaAnalysisAutomationPath,
  mediaAnalysisHistoryPath,
  mediaAnalysisPath,
  mediaDashboardPath,
  mediaGscLinkagePath,
  modelTuningPath,
  operatorListPath,
  orderHistoryPath,
  permissionTemplateListPath,
  personaAssignmentPath,
  personaCatalogPath,
  personaHealthPath,
  personaLibraryPath,
  personaTrendPath,
  productInfoUsagePath,
  productUnderstandingPath,
  promotionKarteConfigPath,
  promotionOutboxPath,
  proposal2Path,
  proposal3Path,
  proposal4Path,
  proposalPath,
  searchSuggestAnalyticsPath,
  searchSuggestDictionaryPath,
  searchSuggestSettingsPath,
  searchTagCandidatesPath,
  searchTagDashboardPath,
  searchTagDeliveryPath,
  searchTagListPath,
  searchTagOptimizationsPath,
  searchTagSettingsPath,
  setupGuidePath,
  siteDashboardPath,
  siteListPath,
  siteOperatorListPath,
  siteUsagePath,
  supportSpecPath,
  systemConfigPath,
  tagsPath,
  targetListPath,
  ugcDashboardPath,
  w2AbTestItemPath,
  w2AbTestPath,
  w2AccessLogsPath,
  w2AdvCodeLogPath,
  w2AdvCodeMediaTypePath,
  w2AdvCodesPath,
  w2CartPath,
  w2CommerceLinkPath,
  w2CouponSchedulePath,
  w2CouponUseUserPath,
  w2CouponsPath,
  w2ExProductSpecsPath,
  w2ExProductVariationSpecsPath,
  w2FeatureAreaBannerPath,
  w2FeatureAreaPath,
  w2FeatureCategoriesPath,
  w2FeatureContentsPath,
  w2FeaturesPath,
  w2FixedPurchaseCancelReasonPath,
  w2FixedPurchaseHistoryPath,
  w2FixedPurchaseItemPath,
  w2FixedPurchasePath,
  w2MailClickLogPath,
  w2MailClickPath,
  w2MailDistSentUserPath,
  w2MailDistSettingPath,
  w2MailSendLogPath,
  w2MemberRankRulePath,
  w2NoveltyGrantConditionsPath,
  w2NoveltyGrantItemPath,
  w2NoveltyPath,
  w2NoveltyTargetItemPath,
  w2OrderCouponsPath,
  w2OrderDetailsPath,
  w2OrderSetPromotionItemPath,
  w2OrderSetPromotionPath,
  w2OrderShippingPath,
  w2OrderWorkflowScenarioSettingPath,
  w2OrdersPath,
  w2PageDesignGroupPath,
  w2PageDesignPath,
  w2PartsDesignGroupPath,
  w2PartsDesignPath,
  w2PointRulePath,
  w2PointRuleSchedulePath,
  w2PopupDisplayPageSettingPath,
  w2PopupPath,
  w2PopupReportPath,
  w2ProductBrandsPath,
  w2ProductBundleItemPath,
  w2ProductBundlePath,
  w2ProductCategoriesPath,
  w2ProductGroupItemsPath,
  w2ProductGroupsPath,
  w2ProductInventoryPath,
  w2ProductLinksPath,
  w2ProductPricePath,
  w2ProductSalePath,
  w2ProductSalePricePath,
  w2ProductSetItemPath,
  w2ProductSetPath,
  w2ProductTagSettingsPath,
  w2ProductTagsPath,
  w2ProductVariationPath,
  w2ProductsPath,
  w2ReviewSyncPath,
  w2SendStatusPath,
  w2SetPromotionItemPath,
  w2SetPromotionPath,
  w2ShopShippingPath,
  w2ShopShippingSizeThresholdPath,
  w2ShopShippingZonePath,
  w2UserActivityPath,
  w2UserAttributesPath,
  w2UserCouponHistoryPath,
  w2UserCouponPath,
  w2UserMemberRankHistoryPath,
  w2UserPointHistoryPath,
  w2UserPointPath,
  w2UsersPath,
  widgetListPath,
  widgetPageListPath
} from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { UGC_MENU, UgcMenuEntry, isUgcMenuGroup } from '@/common/app/ugcMenu'
import { MasterType } from '@/common/model/master/w2Master'
import { ChildProps } from '@/common/util/props'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import IconButton from '@/components/view/atoms/button/IconButton'
import DisclosureRow from '@/components/view/atoms/disclosure/DisclosureRow'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import { ModuleIconType } from '@/components/view/atoms/icon/ModuleIcon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import DisclosureWrapper from '@/components/view/layouts/disclosure/DisclosureWrapper'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Navigation from '@/components/view/layouts/nav/Navigation'
import NavigationList from '@/components/view/layouts/nav/NavigationList'
import NavigationChildRow from '@/components/view/molecules/nav/NavigationChildRow'
import NavigationRow from '@/components/view/molecules/nav/NavigationRow'
import { NavigationColor } from '@/components/view/utils/nav/navigation'

const SiteSelector = () => {
  const siteId = useSiteId()
  const [isShowing, setIsShowing] = useState<boolean>(false)
  const { data: sites } = useSiteListApi({})

  return (
    <DisclosureWrapper
      disclosureNode={
        <>
          {sites?.map((site) => (
            <DisclosureRow
              key={site.id}
              link={siteDashboardPath(site.id)}
              disabled={site.id === siteId}
            >
              <ItemAlign gapX={2} justify='between' direction='horizon' nowrap>
                <Text color={site.id === siteId ? TextColor.DISABLED : TextColor.PRIMARY} truncate>
                  {site.name}
                </Text>
                <Badge badgeType={site.is_test ? BadgeType.SUCCESS : BadgeType.CRITICAL} fixed>
                  {site.is_test ? 'テスト環境' : '本番環境'}
                </Badge>
              </ItemAlign>
            </DisclosureRow>
          ))}
        </>
      }
      off={!isShowing}
      onClickOutSide={() => setIsShowing(false)}
    >
      <Button
        color={ButtonColor.SECONDARY}
        size={'icon'}
        onClick={() => setIsShowing((isShowing) => !isShowing)}
      >
        <ItemAlign gapX={2}>
          <Icon type={IconType.WEB_PAGE} color={IconColor.PRIMARY} size={16} />
          <Icon type={IconType.ARROWS_UP_DOWN} color={IconColor.PRIMARY} size={16} />
        </ItemAlign>
      </Button>
    </DisclosureWrapper>
  )
}

interface ParentRowProps {
  link: string | undefined
  iconType: IconType
  selected?: boolean
  onClick?: () => void
  subTitle?: ModuleIconType
}

const AppNavigationParentRow = ({
  children,
  link,
  color,
  iconType,
  selected,
  onClick,
  subTitle,
  withChild
}: ParentRowProps & ChildProps & { color: NavigationColor | undefined; withChild: boolean }) => {
  return (
    <NavigationRow
      link={link}
      color={color}
      icon={iconType}
      selected={selected && withChild}
      viewing={selected && !withChild}
      onClick={onClick}
      subTitle={subTitle}
      isOnlyDisplaying={link == undefined}
    >
      {children}
    </NavigationRow>
  )
}

interface ChildSegmentProps {
  item: {
    link: string | undefined
    name: string
    selected: boolean
    childItems: {
      link: string | undefined
      name: string
      selected: boolean
    }[]
  }
  setPath: Dispatch<SetStateAction<string>>
  color?: NavigationColor
}

const AppNavigationChildSegment = ({ item, setPath, color }: ChildSegmentProps) => {
  return (
    <NavigationList
      key={item.name}
      color={color}
      parent={
        <NavigationChildRow
          key={item.name}
          color={color}
          link={item.link}
          viewing={item.selected}
          selected={item.childItems.some((child) => child.selected)}
          onClick={item.link ? () => setPath(item.link as string) : undefined}
        >
          {item.name}
        </NavigationChildRow>
      }
      opening={item.selected || item.childItems.some((child) => child.selected)}
      grandChild
    >
      {item.childItems.map((childItem) => (
        <NavigationChildRow
          key={childItem.name}
          color={color}
          link={childItem.link}
          viewing={childItem.selected}
          onClick={childItem.link ? () => setPath(childItem.link as string) : undefined}
          grandChild
        >
          {childItem.name}
        </NavigationChildRow>
      ))}
    </NavigationList>
  )
}

interface SegmentProps extends ChildProps {
  color?: NavigationColor
  parent: ParentRowProps
  items: {
    link: string
    name: string
    selected: boolean
    childItems?: {
      link: string | undefined
      name: string
      selected: boolean
    }[]
  }[]
  setPath: Dispatch<SetStateAction<string>>
  pathName: string
}

const AppNavigationSegment = ({
  children,
  color,
  parent,
  items,
  setPath,
  pathName
}: SegmentProps) => {
  const itemSelected =
    items.some((item) => item.selected) ||
    items.some((item) => item.childItems?.some((child) => child.selected))

  const [isOpening, setIsOpening] = useState<boolean>(!!itemSelected || !!parent.selected)

  useEffect(() => {
    setIsOpening(!!itemSelected || !!parent.selected)
  }, [pathName])

  return (
    <NavigationList
      color={parent.link != undefined || itemSelected ? color : undefined}
      parent={
        <AppNavigationParentRow
          {...parent}
          selected={isOpening}
          color={color}
          onClick={() =>
            parent.link ? setPath(parent.link) : setIsOpening((isOpening) => !isOpening)
          }
          withChild={items.length > 0}
        >
          {children}
        </AppNavigationParentRow>
      }
      opening={isOpening || parent.link == undefined}
    >
      {items.map((item) =>
        item.childItems ? (
          <AppNavigationChildSegment
            key={item.name}
            item={{
              ...item,
              // for typescript
              childItems: item.childItems ?? []
            }}
            setPath={setPath}
            color={color}
          />
        ) : (
          <NavigationChildRow
            key={item.name}
            color={color}
            link={item.link}
            viewing={item.selected}
            onClick={() => setPath(item.link)}
          >
            {item.name}
          </NavigationChildRow>
        )
      )}
    </NavigationList>
  )
}

const AppNavigation = () => {
  const siteId = useSiteId()
  const { pathname: defaultPathName } = useLocation()
  const [pathName, setPathName] = useState<string>(defaultPathName)
  const { operatorMe, isLoading: isMeLoading, isAdmin } = useAuthorizedMe()

  const isSitePage = pathName.startsWith('/sites/' + siteId + '/')

  // サイト内権限: 全権アクセスは admin と site_owner。それ以外は
  // permission_template.menu_permissions[menu_key] に従う。
  const isSiteOwner = operatorMe?.role === 'site_owner'
  const hasFullAccess = isAdmin || isSiteOwner
  const menuPerms = (operatorMe?.menu_permissions || {}) as Record<string, boolean>
  const canShow = (menuKey: string): boolean => hasFullAccess || !!menuPerms[menuKey]

  useEffect(() => {
    setPathName(defaultPathName)
  }, [defaultPathName])

  const {
    data: site,
    isLoading: isSiteLoading,
    error: siteError
  } = useSiteGetApi(
    isSitePage
      ? {
          site_id: siteId
        }
      : undefined
  )

  return (
    <Navigation
      userName={site ? site.name : undefined}
      isTest={isSitePage && site?.is_test}
      top={
        isAdmin && isSitePage && site ? (
          <NavigationRow link={siteListPath()} icon={IconType.WEB_PAGE} subNode={<SiteSelector />}>
            サイト管理
          </NavigationRow>
        ) : undefined
      }
      back={
        isAdmin && isSitePage && site ? (
          <IconButton
            type={IconType.ARROW_BACK}
            text={'管理者モード'}
            link={dashboardPath()}
            color={TextColor.LINK}
            iconColor={IconColor.LINK}
            size={12}
          />
        ) : undefined
      }
      bottom={
        operatorMe && (
          <>
            {!!siteId && (
              <>
                <AppNavigationSegment
                  color={NavigationColor.BRAND}
                  parent={{
                    link: siteOperatorListPath(siteId),
                    iconType: IconType.STORE,
                    selected: false
                  }}
                  items={[
                    {
                      link: siteOperatorListPath(siteId),
                      name: 'オペレーター管理',
                      selected: pathName.startsWith(siteOperatorListPath(siteId))
                    },
                    {
                      link: permissionTemplateListPath(siteId),
                      name: '権限管理',
                      selected: pathName.startsWith(permissionTemplateListPath(siteId))
                    }
                  ]}
                  setPath={setPathName}
                  pathName={pathName}
                >
                  サイト設定
                </AppNavigationSegment>
              </>
            )}
          </>
        )
      }
    >
      {!operatorMe && (isMeLoading || isSiteLoading) && (
        <div className='flex flex-col'>
          {[...Array(isAdmin ? 10 : 12)].map((_, index) => (
            <NavigationRow key={index} isLoading>
              <></>
            </NavigationRow>
          ))}
        </div>
      )}
      {operatorMe && isAdmin && !isSitePage && (
        <>
          <AppNavigationSegment
            color={NavigationColor.BRAND}
            parent={{
              link: dashboardPath(),
              iconType: IconType.DASHBOARD,
              selected: pathName == dashboardPath()
            }}
            items={[]}
            setPath={setPathName}
            pathName={pathName}
          >
            ダッシュボード
          </AppNavigationSegment>
          <AppNavigationSegment
            color={NavigationColor.BRAND}
            parent={{
              link: siteListPath(),
              iconType: IconType.WEB_PAGE,
              selected: pathName.startsWith(siteListPath())
            }}
            items={[]}
            setPath={setPathName}
            pathName={pathName}
          >
            サイト管理
          </AppNavigationSegment>
          <AppNavigationSegment
            color={NavigationColor.BRAND}
            parent={{
              link: analyticsPath(),
              iconType: IconType.CHART_COLUMN,
              selected: pathName.startsWith(analyticsPath())
            }}
            items={[]}
            setPath={setPathName}
            pathName={pathName}
          >
            アナリティクス
          </AppNavigationSegment>
          <AppNavigationSegment
            color={NavigationColor.BRAND}
            parent={{
              link: undefined,
              iconType: IconType.GROUP
            }}
            items={[
              {
                link: proposalPath(),
                name: 'Reco-Media',
                selected: pathName == proposalPath()
              },
              {
                name: 'Media-DRIVER',
                link: proposal2Path(),
                selected: pathName.startsWith(proposal2Path())
              },
              {
                name: 'Findee',
                link: proposal3Path(),
                selected: pathName.startsWith(proposal3Path())
              },
              {
                name: 'AIペルソナ',
                link: proposal4Path(),
                selected: pathName.startsWith(proposal4Path())
              }
            ]}
            setPath={setPathName}
            pathName={pathName}
          >
            営業紹介資料
          </AppNavigationSegment>
          <AppNavigationSegment
            color={NavigationColor.BRAND}
            parent={{
              link: undefined,
              iconType: IconType.HEADSET
            }}
            items={[
              {
                link: w2CommerceLinkPath(),
                name: 'W2Commerceリンク指定',
                selected: pathName.startsWith(w2CommerceLinkPath())
              },
              {
                name: 'ペルソナカタログ',
                link: personaCatalogPath(),
                selected: pathName.startsWith(personaCatalogPath())
              },
              {
                link: promotionKarteConfigPath(),
                name: 'プロモーションカルテ連携設定',
                selected: pathName.startsWith(promotionKarteConfigPath())
              },
              {
                link: siteUsagePath(),
                name: 'サイト状況',
                selected: pathName.startsWith(siteUsagePath())
              }
            ]}
            setPath={setPathName}
            pathName={pathName}
          >
            運用管理
          </AppNavigationSegment>
          {/* 運用管理が肥大化したため、システム全体の設定・統制はこちらへ分離 */}
          <AppNavigationSegment
            color={NavigationColor.BRAND}
            parent={{
              link: undefined,
              iconType: IconType.SETTINGS
            }}
            items={[
              {
                link: systemConfigPath(),
                name: 'システム設定',
                selected: pathName.startsWith(systemConfigPath())
              },
              {
                name: '監査ログ',
                link: auditLogsPath(),
                selected: pathName.startsWith(auditLogsPath())
              },
              {
                name: 'オペレーター管理',
                link: operatorListPath(),
                selected: pathName.startsWith('/operators')
              },
              {
                name: '連携APIキー管理',
                link: linkageApiKeyListPath(),
                selected: pathName.startsWith(linkageApiKeyListPath())
              }
            ]}
            setPath={setPathName}
            pathName={pathName}
          >
            システム管理
          </AppNavigationSegment>
          <AppNavigationSegment
            color={NavigationColor.BRAND}
            parent={{
              link: undefined,
              iconType: IconType.BUILDING
            }}
            items={[
              {
                link: billingPath(),
                name: '請求管理',
                selected: pathName.startsWith(billingPath())
              }
            ]}
            setPath={setPathName}
            pathName={pathName}
          >
            顧客契約管理
          </AppNavigationSegment>
          <AppNavigationSegment
            color={NavigationColor.BRAND}
            parent={{
              link: undefined,
              iconType: IconType.ALL_USERS
            }}
            items={[
              {
                link: supportSpecPath(),
                name: '機能概要（仕様検索）',
                selected: pathName.startsWith(supportSpecPath())
              },
              {
                link: featureListPath(),
                name: '機能一覧',
                selected: pathName.startsWith(featureListPath())
              }
            ]}
            setPath={setPathName}
            pathName={pathName}
          >
            社内サポート/製品理解
          </AppNavigationSegment>
        </>
      )}
      {operatorMe && !!siteId && isSitePage && site && (
        <>
          {/* 最上部のダッシュボード項目は AIレコメンドの「ダッシュボード」と
              リンク先が重複していたため削除。AIレコメンド有効時はその配下、
              無効時は機能別ダッシュボード（コンテンツ生成・UGC等）から到達。 */}
          <AppNavigationSegment
            color={NavigationColor.KAKI}
            parent={{
              link: masterSummaryPath(siteId),
              iconType: IconType.DATABASE,
              subTitle: ModuleIconType.W2_MASTER
            }}
            items={[
              {
                link: masterSummaryPath(siteId),
                name: 'マスタデータ状況',
                selected: pathName.startsWith(masterSummaryPath(siteId))
              },
              {
                link: w2UsersPath(siteId),
                name: 'ユーザーデータ',
                selected: false,
                childItems: [
                  {
                    link: w2UsersPath(siteId),
                    name: masterTypeName(MasterType.W2_USER),
                    selected: pathName.startsWith(w2UsersPath(siteId))
                  },
                  {
                    link: w2UserAttributesPath(siteId),
                    name: masterTypeName(MasterType.W2_USER_ATTRIBUTE),
                    selected: pathName.startsWith(w2UserAttributesPath(siteId))
                  },
                  {
                    link: w2AccessLogsPath(siteId),
                    name: masterTypeName(MasterType.W2_ACCESS_LOG),
                    selected: pathName.startsWith(w2AccessLogsPath(siteId))
                  },
                  {
                    link: w2UserPointPath(siteId),
                    name: masterTypeName(MasterType.W2_USER_POINT),
                    selected: pathName.startsWith(w2UserPointPath(siteId))
                  },
                  {
                    link: w2UserPointHistoryPath(siteId),
                    name: masterTypeName(MasterType.W2_USER_POINT_HISTORY),
                    selected: pathName.startsWith(w2UserPointHistoryPath(siteId))
                  },
                  {
                    link: w2PointRulePath(siteId),
                    name: masterTypeName(MasterType.W2_POINT_RULE),
                    selected: pathName.startsWith(w2PointRulePath(siteId))
                  },
                  {
                    link: w2PointRuleSchedulePath(siteId),
                    name: masterTypeName(MasterType.W2_POINT_RULE_SCHEDULE),
                    selected: pathName.startsWith(w2PointRuleSchedulePath(siteId))
                  },
                  {
                    link: w2UserMemberRankHistoryPath(siteId),
                    name: masterTypeName(MasterType.W2_USER_MEMBER_RANK_HISTORY),
                    selected: pathName.startsWith(w2UserMemberRankHistoryPath(siteId))
                  },
                  {
                    link: w2MemberRankRulePath(siteId),
                    name: masterTypeName(MasterType.W2_MEMBER_RANK_RULE),
                    selected: pathName.startsWith(w2MemberRankRulePath(siteId))
                  },
                  {
                    link: w2FixedPurchasePath(siteId),
                    name: masterTypeName(MasterType.W2_FIXED_PURCHASE),
                    selected: pathName.startsWith(w2FixedPurchasePath(siteId))
                  },
                  {
                    link: w2FixedPurchaseItemPath(siteId),
                    name: masterTypeName(MasterType.W2_FIXED_PURCHASE_ITEM),
                    selected: pathName.startsWith(w2FixedPurchaseItemPath(siteId))
                  },
                  {
                    link: w2FixedPurchaseHistoryPath(siteId),
                    name: masterTypeName(MasterType.W2_FIXED_PURCHASE_HISTORY),
                    selected: pathName.startsWith(w2FixedPurchaseHistoryPath(siteId))
                  },
                  {
                    link: w2FixedPurchaseCancelReasonPath(siteId),
                    name: masterTypeName(MasterType.W2_FIXED_PURCHASE_CANCEL_REASON),
                    selected: pathName.startsWith(w2FixedPurchaseCancelReasonPath(siteId))
                  },
                  {
                    link: w2UserActivityPath(siteId),
                    name: masterTypeName(MasterType.W2_USER_ACTIVITY),
                    selected: pathName.startsWith(w2UserActivityPath(siteId))
                  },
                  {
                    link: w2MailDistSettingPath(siteId),
                    name: masterTypeName(MasterType.W2_MAIL_DIST_SETTING),
                    selected: pathName.startsWith(w2MailDistSettingPath(siteId))
                  },
                  {
                    link: w2MailDistSentUserPath(siteId),
                    name: masterTypeName(MasterType.W2_MAIL_DIST_SENT_USER),
                    selected: pathName.startsWith(w2MailDistSentUserPath(siteId))
                  },
                  {
                    link: w2MailSendLogPath(siteId),
                    name: masterTypeName(MasterType.W2_MAIL_SEND_LOG),
                    selected: pathName.startsWith(w2MailSendLogPath(siteId))
                  },
                  {
                    link: w2MailClickPath(siteId),
                    name: masterTypeName(MasterType.W2_MAIL_CLICK),
                    selected: pathName.startsWith(w2MailClickPath(siteId))
                  },
                  {
                    link: w2MailClickLogPath(siteId),
                    name: masterTypeName(MasterType.W2_MAIL_CLICK_LOG),
                    selected: pathName.startsWith(w2MailClickLogPath(siteId))
                  },
                  {
                    link: w2OrderWorkflowScenarioSettingPath(siteId),
                    name: masterTypeName(MasterType.W2_ORDER_WORKFLOW_SCENARIO_SETTING),
                    selected: pathName.startsWith(w2OrderWorkflowScenarioSettingPath(siteId))
                  }
                ]
              },
              {
                link: w2ProductsPath(siteId),
                name: '商品データ',
                selected: pathName.startsWith(w2ProductsPath(siteId)),
                childItems: [
                  {
                    link: w2ProductsPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT),
                    selected: pathName.startsWith(w2ProductsPath(siteId))
                  },
                  {
                    link: w2ProductLinksPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_LINK),
                    selected: pathName.startsWith(w2ProductLinksPath(siteId))
                  },
                  // 拡張マスタ（add-w2-ex-product-spec で追加）：商品リンクの直後に置く
                  {
                    link: w2ExProductSpecsPath(siteId),
                    name: masterTypeName(MasterType.W2_EX_PRODUCT_SPEC),
                    selected: pathName.startsWith(w2ExProductSpecsPath(siteId))
                  },
                  {
                    link: w2ExProductVariationSpecsPath(siteId),
                    name: masterTypeName(MasterType.W2_EX_PRODUCT_VARIATION_SPEC),
                    selected: pathName.startsWith(w2ExProductVariationSpecsPath(siteId))
                  },
                  {
                    link: w2ProductBrandsPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_BRAND),
                    selected: pathName.startsWith(w2ProductBrandsPath(siteId))
                  },
                  {
                    link: w2ProductCategoriesPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_CATEGORY),
                    selected: pathName.startsWith(w2ProductCategoriesPath(siteId))
                  },
                  {
                    link: w2ProductTagsPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_TAG),
                    selected: pathName.startsWith(w2ProductTagsPath(siteId))
                  },
                  // 商品タグの列名の定義。商品タグの直後に置く（add-w2-product-tag-setting）
                  {
                    link: w2ProductTagSettingsPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_TAG_SETTING),
                    selected: pathName.startsWith(w2ProductTagSettingsPath(siteId))
                  },
                  {
                    link: w2ProductGroupsPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_GROUP),
                    selected: pathName.startsWith(w2ProductGroupsPath(siteId))
                  },
                  {
                    link: w2ProductGroupItemsPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_GROUP_ITEM),
                    selected: pathName.startsWith(w2ProductGroupItemsPath(siteId))
                  },
                  {
                    link: w2ProductInventoryPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_STOCK),
                    selected: pathName.startsWith(w2ProductInventoryPath(siteId))
                  },
                  {
                    link: w2ProductVariationPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_VARIATION),
                    selected: pathName.startsWith(w2ProductVariationPath(siteId))
                  },
                  {
                    link: w2ProductPricePath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_PRICE),
                    selected: pathName.startsWith(w2ProductPricePath(siteId))
                  },
                  {
                    link: w2NoveltyPath(siteId),
                    name: masterTypeName(MasterType.W2_NOVELTY),
                    selected:
                      pathName == w2NoveltyPath(siteId) ||
                      pathName.startsWith(w2NoveltyPath(siteId) + '/')
                  },
                  {
                    link: w2NoveltyGrantConditionsPath(siteId),
                    name: masterTypeName(MasterType.W2_NOVELTY_GRANT_CONDITIONS),
                    selected: pathName.startsWith(w2NoveltyGrantConditionsPath(siteId))
                  },
                  {
                    link: w2NoveltyGrantItemPath(siteId),
                    name: masterTypeName(MasterType.W2_NOVELTY_GRANT_ITEM),
                    selected: pathName.startsWith(w2NoveltyGrantItemPath(siteId))
                  },
                  {
                    link: w2NoveltyTargetItemPath(siteId),
                    name: masterTypeName(MasterType.W2_NOVELTY_TARGET_ITEM),
                    selected: pathName.startsWith(w2NoveltyTargetItemPath(siteId))
                  },
                  {
                    link: w2ProductSalePath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_SALE),
                    selected: pathName.startsWith(w2ProductSalePath(siteId))
                  },
                  {
                    link: w2ProductSalePricePath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_SALE_PRICE),
                    selected: pathName.startsWith(w2ProductSalePricePath(siteId))
                  }
                ]
              },
              {
                link: w2OrdersPath(siteId),
                name: '受注データ',
                selected: pathName.startsWith(w2OrdersPath(siteId)),
                childItems: [
                  {
                    link: w2OrdersPath(siteId),
                    name: masterTypeName(MasterType.W2_ORDER),
                    selected: pathName.startsWith(w2OrdersPath(siteId))
                  },
                  {
                    link: w2OrderDetailsPath(siteId),
                    name: masterTypeName(MasterType.W2_ORDER_ITEM),
                    selected: pathName.startsWith(w2OrderDetailsPath(siteId))
                  },
                  {
                    link: w2CartPath(siteId),
                    name: masterTypeName(MasterType.W2_CART),
                    selected: pathName.startsWith(w2CartPath(siteId))
                  },
                  {
                    link: w2OrderCouponsPath(siteId),
                    name: masterTypeName(MasterType.W2_ORDER_COUPON),
                    selected: pathName.startsWith(w2OrderCouponsPath(siteId))
                  },
                  {
                    link: w2CouponsPath(siteId),
                    name: masterTypeName(MasterType.W2_COUPON),
                    selected: pathName.startsWith(w2CouponsPath(siteId))
                  },
                  {
                    link: w2OrderShippingPath(siteId),
                    name: masterTypeName(MasterType.W2_ORDER_SHIPPING),
                    selected: pathName.startsWith(w2OrderShippingPath(siteId))
                  },
                  {
                    link: w2ShopShippingPath(siteId),
                    name: masterTypeName(MasterType.W2_SHOP_SHIPPING),
                    selected:
                      pathName == w2ShopShippingPath(siteId) ||
                      pathName.startsWith(w2ShopShippingPath(siteId) + '/')
                  },
                  {
                    link: w2ShopShippingZonePath(siteId),
                    name: masterTypeName(MasterType.W2_SHOP_SHIPPING_ZONE),
                    selected: pathName.startsWith(w2ShopShippingZonePath(siteId))
                  },
                  {
                    link: w2ShopShippingSizeThresholdPath(siteId),
                    name: masterTypeName(MasterType.W2_SHOP_SHIPPING_SIZE_THRESHOLD),
                    selected: pathName.startsWith(w2ShopShippingSizeThresholdPath(siteId))
                  },
                  {
                    link: w2OrderSetPromotionPath(siteId),
                    name: masterTypeName(MasterType.W2_ORDER_SET_PROMOTION),
                    selected:
                      pathName == w2OrderSetPromotionPath(siteId) ||
                      pathName.startsWith(w2OrderSetPromotionPath(siteId) + '/')
                  },
                  {
                    link: w2OrderSetPromotionItemPath(siteId),
                    name: masterTypeName(MasterType.W2_ORDER_SET_PROMOTION_ITEM),
                    selected:
                      pathName == w2OrderSetPromotionItemPath(siteId) ||
                      pathName.startsWith(w2OrderSetPromotionItemPath(siteId) + '/')
                  },
                  {
                    link: w2SetPromotionPath(siteId),
                    name: masterTypeName(MasterType.W2_SET_PROMOTION),
                    selected:
                      pathName == w2SetPromotionPath(siteId) ||
                      pathName.startsWith(w2SetPromotionPath(siteId) + '/')
                  },
                  {
                    link: w2SetPromotionItemPath(siteId),
                    name: masterTypeName(MasterType.W2_SET_PROMOTION_ITEM),
                    selected:
                      pathName == w2SetPromotionItemPath(siteId) ||
                      pathName.startsWith(w2SetPromotionItemPath(siteId) + '/')
                  },
                  {
                    link: w2ProductBundlePath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_BUNDLE),
                    selected:
                      pathName == w2ProductBundlePath(siteId) ||
                      pathName.startsWith(w2ProductBundlePath(siteId) + '/')
                  },
                  {
                    link: w2ProductBundleItemPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_BUNDLE_ITEM),
                    selected:
                      pathName == w2ProductBundleItemPath(siteId) ||
                      pathName.startsWith(w2ProductBundleItemPath(siteId) + '/')
                  },
                  {
                    link: w2ProductSetPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_SET),
                    selected:
                      pathName == w2ProductSetPath(siteId) ||
                      pathName.startsWith(w2ProductSetPath(siteId) + '/')
                  },
                  {
                    link: w2ProductSetItemPath(siteId),
                    name: masterTypeName(MasterType.W2_PRODUCT_SET_ITEM),
                    selected:
                      pathName == w2ProductSetItemPath(siteId) ||
                      pathName.startsWith(w2ProductSetItemPath(siteId) + '/')
                  },
                  {
                    link: w2UserCouponPath(siteId),
                    name: masterTypeName(MasterType.W2_USER_COUPON),
                    selected:
                      pathName == w2UserCouponPath(siteId) ||
                      pathName.startsWith(w2UserCouponPath(siteId) + '/')
                  },
                  {
                    link: w2UserCouponHistoryPath(siteId),
                    name: masterTypeName(MasterType.W2_USER_COUPON_HISTORY),
                    selected: pathName.startsWith(w2UserCouponHistoryPath(siteId))
                  },
                  {
                    link: w2CouponUseUserPath(siteId),
                    name: masterTypeName(MasterType.W2_COUPON_USE_USER),
                    selected: pathName.startsWith(w2CouponUseUserPath(siteId))
                  },
                  {
                    link: w2CouponSchedulePath(siteId),
                    name: masterTypeName(MasterType.W2_COUPON_SCHEDULE),
                    selected: pathName.startsWith(w2CouponSchedulePath(siteId))
                  }
                ]
              },
              {
                link: w2FeaturesPath(siteId),
                name: 'コンテンツデータ',
                selected: pathName.startsWith(w2FeaturesPath(siteId)),
                childItems: [
                  {
                    link: w2AdvCodesPath(siteId),
                    name: masterTypeName(MasterType.W2_ADV_CODE),
                    selected: pathName.startsWith(w2AdvCodesPath(siteId))
                  },
                  {
                    link: w2FeaturesPath(siteId),
                    name: masterTypeName(MasterType.W2_FEATURE_PAGE),
                    selected: pathName.startsWith(w2FeaturesPath(siteId))
                  },
                  {
                    link: w2FeatureCategoriesPath(siteId),
                    name: masterTypeName(MasterType.W2_FEATURE_PAGE_CATEGORY),
                    selected: pathName.startsWith(w2FeatureCategoriesPath(siteId))
                  },
                  {
                    link: w2FeatureContentsPath(siteId),
                    name: masterTypeName(MasterType.W2_FEATURE_PAGE_CONTENTS),
                    selected: pathName.startsWith(w2FeatureContentsPath(siteId))
                  },
                  {
                    link: w2PageDesignPath(siteId),
                    name: masterTypeName(MasterType.W2_PAGE_DESIGN),
                    selected:
                      pathName == w2PageDesignPath(siteId) ||
                      pathName.startsWith(w2PageDesignPath(siteId) + '/')
                  },
                  {
                    link: w2PageDesignGroupPath(siteId),
                    name: masterTypeName(MasterType.W2_PAGE_DESIGN_GROUP),
                    selected: pathName.startsWith(w2PageDesignGroupPath(siteId))
                  },
                  {
                    link: w2PartsDesignPath(siteId),
                    name: masterTypeName(MasterType.W2_PARTS_DESIGN),
                    selected:
                      pathName == w2PartsDesignPath(siteId) ||
                      pathName.startsWith(w2PartsDesignPath(siteId) + '/')
                  },
                  {
                    link: w2PartsDesignGroupPath(siteId),
                    name: masterTypeName(MasterType.W2_PARTS_DESIGN_GROUP),
                    selected: pathName.startsWith(w2PartsDesignGroupPath(siteId))
                  },
                  {
                    link: w2FeatureAreaPath(siteId),
                    name: masterTypeName(MasterType.W2_FEATURE_AREA),
                    selected:
                      pathName == w2FeatureAreaPath(siteId) ||
                      pathName.startsWith(w2FeatureAreaPath(siteId) + '/')
                  },
                  {
                    link: w2FeatureAreaBannerPath(siteId),
                    name: masterTypeName(MasterType.W2_FEATURE_AREA_BANNER),
                    selected: pathName.startsWith(w2FeatureAreaBannerPath(siteId))
                  },
                  {
                    link: w2PopupPath(siteId),
                    name: masterTypeName(MasterType.W2_POPUP),
                    selected:
                      pathName == w2PopupPath(siteId) ||
                      pathName.startsWith(w2PopupPath(siteId) + '/')
                  },
                  {
                    link: w2PopupDisplayPageSettingPath(siteId),
                    name: masterTypeName(MasterType.W2_POPUP_DISPLAY_PAGE_SETTING),
                    selected: pathName.startsWith(w2PopupDisplayPageSettingPath(siteId))
                  },
                  {
                    link: w2PopupReportPath(siteId),
                    name: masterTypeName(MasterType.W2_POPUP_REPORT),
                    selected: pathName.startsWith(w2PopupReportPath(siteId))
                  }
                ]
              },
              {
                link: w2AdvCodeLogPath(siteId),
                name: '広告データ',
                selected: pathName.startsWith(w2AdvCodeLogPath(siteId)),
                childItems: [
                  {
                    link: w2AdvCodeLogPath(siteId),
                    name: masterTypeName(MasterType.W2_ADV_CODE_LOG),
                    selected: pathName.startsWith(w2AdvCodeLogPath(siteId))
                  },
                  {
                    link: w2AdvCodeMediaTypePath(siteId),
                    name: masterTypeName(MasterType.W2_ADV_CODE_MEDIA_TYPE),
                    selected: pathName.startsWith(w2AdvCodeMediaTypePath(siteId))
                  },
                  {
                    link: w2AbTestPath(siteId),
                    name: masterTypeName(MasterType.W2_AB_TEST),
                    selected:
                      pathName == w2AbTestPath(siteId) ||
                      pathName.startsWith(w2AbTestPath(siteId) + '/')
                  },
                  {
                    link: w2AbTestItemPath(siteId),
                    name: masterTypeName(MasterType.W2_AB_TEST_ITEM),
                    selected: pathName.startsWith(w2AbTestItemPath(siteId))
                  }
                ]
              },
              {
                link: fileUploadPath(siteId),
                name: 'ファイル取込設定',
                selected: pathName.startsWith(fileUploadPath(siteId))
              },
              // 送信データ（直下に W2Commerce送信データ状況 / ターゲットリスト / プロモーション設定）
              // W2送信設定・ターゲットリスト連携のいずれかONで表示し、各子は自フラグで出し分ける
              ...(site?.enable_w2_send || site?.enable_target_list
                ? [
                    {
                      link: site?.enable_target_list
                        ? targetListPath(siteId)
                        : w2SendStatusPath(siteId),
                      name: '送信データ',
                      selected: false,
                      childItems: [
                        ...(site?.enable_w2_send
                          ? [
                              {
                                link: w2SendStatusPath(siteId),
                                name: 'W2Commerce送信データ状況',
                                selected: pathName.startsWith(w2SendStatusPath(siteId))
                              }
                            ]
                          : []),
                        ...(site?.enable_target_list
                          ? [
                              {
                                link: targetListPath(siteId),
                                name: 'ターゲットリスト',
                                selected: pathName.startsWith(targetListPath(siteId))
                              },
                              {
                                link: promotionOutboxPath(siteId),
                                name: 'プロモーション設定',
                                selected: pathName.startsWith(promotionOutboxPath(siteId))
                              }
                            ]
                          : [])
                      ]
                    }
                  ]
                : []),
              // 外部記事コンテンツ（特集ページマスタに載らない記事ページを取り込み、特集ページと
              // 併せて Media-Driver・記事レコメンド・Findee・AIタグの対象にする）。
              // Media-Driver と Reco-Media のどちらを使えるサイトにも出す（API 側と同じ判定）。
              // Reco-Media だけの契約でも、ここで取り込まないと記事レコメンドが空になるため
              ...((site.enable_content_analysis && canShow('content_analysis')) ||
              (site.enable_reco_media && canShow('reco_media'))
                ? [
                    {
                      link: externalArticlePath(siteId),
                      name: '外部記事コンテンツ',
                      selected: pathName.startsWith(externalArticlePath(siteId))
                    }
                  ]
                : []),
              // W2レビュー連携（承認済みのW2レビューをUGCへ反映）。
              // UGC/レビューを契約しているサイトにだけ出す
              ...(site.enable_review && canShow('review')
                ? [
                    {
                      link: w2ReviewSyncPath(siteId),
                      name: 'W2レビュー連携',
                      selected: pathName.startsWith(w2ReviewSyncPath(siteId))
                    }
                  ]
                : []),
              // データ理解 ＞ 商品意味理解（ページ内タブで カバレッジ・分布 / 商品明細 を切替）
              //             ＞ ベクトル化データ（AI購入支援から移設）
              {
                link: productUnderstandingPath(siteId),
                name: 'データ理解',
                selected: false,
                childItems: [
                  {
                    link: productUnderstandingPath(siteId),
                    name: '商品意味理解',
                    selected: pathName.startsWith(productUnderstandingPath(siteId))
                  },
                  ...(site.enable_ai_purchase_assist && canShow('ai_purchase_assist')
                    ? [
                        {
                          link: aiPurchaseAssistVectorSourcesPath(siteId),
                          name: 'ベクトル化データ',
                          selected: pathName.startsWith(aiPurchaseAssistVectorSourcesPath(siteId))
                        }
                      ]
                    : [])
                ]
              },
              // 商品情報の利用設定。語句検索・意味検索/会話・レコメンドが読む
              // 商品情報を用途ごとに選ぶ。データ理解の下ではなく直下に置くのは、
              // 効く先が Findee と Reco-Media の両方にまたがるため
              {
                link: productInfoUsagePath(siteId),
                name: '商品情報の利用設定',
                selected: pathName.startsWith(productInfoUsagePath(siteId))
              }
            ]}
            setPath={setPathName}
            pathName={pathName}
          >
            データ基盤
          </AppNavigationSegment>
          {site.enable_persona && canShow('ai_persona') && (
            <AppNavigationSegment
              color={NavigationColor.PURPLE}
              parent={{
                link: personaLibraryPath(siteId),
                iconType: IconType.USER_SQUARE
              }}
              items={[
                {
                  link: personaLibraryPath(siteId),
                  name: 'ペルソナライブラリ',
                  selected: pathName.startsWith(personaLibraryPath(siteId))
                },
                {
                  link: personaAssignmentPath(siteId),
                  name: '判定・割当',
                  selected: pathName.startsWith(personaAssignmentPath(siteId))
                },
                {
                  link: personaTrendPath(siteId),
                  name: 'ペルソナ推移',
                  selected: pathName.startsWith(personaTrendPath(siteId))
                },
                {
                  link: personaHealthPath(siteId),
                  name: '属性ヘルス',
                  selected: pathName.startsWith(personaHealthPath(siteId))
                }
              ]}
              setPath={setPathName}
              pathName={pathName}
            >
              AIペルソナ
            </AppNavigationSegment>
          )}
          {site.enable_reco_media && canShow('reco_media') && (
            <AppNavigationSegment
              color={NavigationColor.SKY}
              parent={{
                link: siteDashboardPath(siteId),
                iconType: IconType.RECOMMEND,
                subTitle: ModuleIconType.RECO_MEDIA
              }}
              items={[
                {
                  link: siteDashboardPath(siteId),
                  name: 'ダッシュボード',
                  selected: pathName == siteDashboardPath(siteId),
                  childItems: [
                    {
                      link: deliveryStatsPath(siteId),
                      name: '配信結果/分析',
                      selected: pathName.startsWith(deliveryStatsPath(siteId))
                    }
                  ]
                },
                {
                  link: customersPath(siteId),
                  name: '訪問者履歴',
                  selected: pathName.startsWith(customersPath(siteId))
                },
                {
                  link: behaviorPath(siteId),
                  name: '行動履歴',
                  selected: pathName.startsWith(behaviorPath(siteId))
                },
                {
                  link: orderHistoryPath(siteId),
                  name: '受注履歴',
                  selected: pathName.startsWith(orderHistoryPath(siteId))
                },
                {
                  link: widgetListPath(siteId),
                  name: 'レコメンドパーツ管理',
                  selected: pathName.startsWith(widgetListPath(siteId)),
                  childItems: pathName.startsWith(
                    `/sites/${siteId}/recommend/distribution/ab-campaign/`
                  )
                    ? [
                        {
                          link: undefined,
                          name: 'A/Bテスト管理',
                          selected: true
                        }
                      ]
                    : undefined
                },
                {
                  link: distributionListPath(siteId),
                  name: '配信管理',
                  selected: pathName.startsWith(distributionListPath(siteId))
                },
                {
                  link: modelTuningPath(siteId),
                  name: 'AIモデルチューニング',
                  selected: pathName.startsWith(modelTuningPath(siteId))
                },
                {
                  link: aiTagsPath(siteId),
                  name: 'AIタグ分析',
                  selected: pathName.startsWith(aiTagsPath(siteId))
                },
                {
                  link: setupGuidePath(siteId),
                  name: '設定',
                  selected: pathName.startsWith(setupGuidePath(siteId)),
                  childItems: [
                    {
                      link: setupGuidePath(siteId),
                      name: '設定ガイド',
                      selected: pathName.startsWith(setupGuidePath(siteId))
                    },
                    {
                      link: tagsPath(siteId),
                      name: '埋め込みコード',
                      selected: pathName.startsWith(tagsPath(siteId))
                    },
                    {
                      link: widgetPageListPath(siteId),
                      name: '設置ページ管理',
                      selected: pathName.startsWith(widgetPageListPath(siteId))
                    }
                  ]
                }
              ]}
              setPath={setPathName}
              pathName={pathName}
            >
              AIレコメンド
            </AppNavigationSegment>
          )}
          {site.enable_content_analysis && canShow('content_analysis') && (
            <AppNavigationSegment
              color={NavigationColor.GREEN}
              parent={{
                link: mediaDashboardPath(siteId),
                iconType: IconType.WRITING,
                subTitle: ModuleIconType.MEDIA_DRIVER
              }}
              items={[
                {
                  link: mediaDashboardPath(siteId),
                  name: 'ダッシュボード',
                  selected: pathName.startsWith(mediaDashboardPath(siteId))
                },
                {
                  link: mediaAnalysisPath(siteId),
                  name: 'サイト現状分析',
                  selected: pathName == mediaAnalysisPath(siteId),
                  childItems: [
                    {
                      link: mediaAnalysisPath(siteId),
                      name: '分析一覧',
                      selected: pathName == mediaAnalysisPath(siteId)
                    },
                    {
                      link: mediaAnalysisHistoryPath(siteId),
                      name: '分析履歴一覧',
                      selected: pathName.startsWith(mediaAnalysisPath(siteId) + '/')
                    }
                  ]
                },
                {
                  link: mediaActionPath(siteId),
                  name: '推奨アクション管理',
                  selected: pathName.startsWith(mediaActionPath(siteId))
                },
                {
                  link: articleListPath(siteId),
                  name: '記事管理',
                  selected: pathName == articleListPath(siteId),
                  childItems: [
                    {
                      link: articleListPath(siteId),
                      name: '記事一覧',
                      selected:
                        pathName == articleListPath(siteId) ||
                        (pathName.startsWith(articleListPath(siteId) + '/') &&
                          !pathName.startsWith(articleAnalysisListPath(siteId)) &&
                          !pathName.startsWith(articleApprovalListPath(siteId)) &&
                          !pathName.startsWith(articleNewPath(siteId)))
                    },
                    {
                      link: articleAnalysisListPath(siteId),
                      name: '記事診断結果一覧',
                      selected: pathName.startsWith(articleAnalysisListPath(siteId))
                    },
                    ...(site.enable_article_upload && canShow('article_upload')
                      ? [
                          {
                            link: articleApprovalListPath(siteId),
                            name: '承認リクエスト一覧',
                            selected: pathName.startsWith(articleApprovalListPath(siteId))
                          }
                        ]
                      : [])
                  ]
                },
                {
                  link: articleNewPath(siteId),
                  name: 'AI記事作成',
                  selected: pathName.startsWith(articleNewPath(siteId))
                },
                {
                  link: mediaAnalysisAutomationPath(siteId),
                  name: '設定',
                  selected: pathName.startsWith(mediaAnalysisAutomationPath(siteId)),
                  childItems: [
                    {
                      link: mediaAnalysisAutomationPath(siteId),
                      name: '分析自動実行',
                      selected: pathName.startsWith(mediaAnalysisAutomationPath(siteId))
                    },
                    {
                      link: analysisTargetPath(siteId),
                      name: '分析対象管理',
                      selected: pathName.startsWith(analysisTargetPath(siteId))
                    },
                    {
                      link: mediaAiTrainingDataPath(siteId),
                      name: 'AI学習用データ',
                      selected: pathName.startsWith(mediaAiTrainingDataPath(siteId))
                    },
                    {
                      link: mediaGscLinkagePath(siteId),
                      name: 'GSC連携',
                      selected: pathName.startsWith(mediaGscLinkagePath(siteId))
                    }
                  ]
                }
              ]}
              setPath={setPathName}
              pathName={pathName}
            >
              コンテンツ生成
            </AppNavigationSegment>
          )}
          {site.enable_review && canShow('review') && (
            <AppNavigationSegment
              color={NavigationColor.PURPLE}
              parent={{
                link: ugcDashboardPath(siteId),
                iconType: IconType.REVIEW_HEART,
                subTitle: ModuleIconType.UGC_INTELLIGENCE
              }}
              // 2026-09-29 変更：メニューは common/app/ugcMenu.ts の定義から組み立てる
              // （設定ガイドも同じ定義を見て、メニューにある機能だけを案内する）
              items={UGC_MENU.map((node) => {
                const toItem = (e: UgcMenuEntry) => ({
                  link: e.path(siteId),
                  name: e.name,
                  selected: e.exact
                    ? pathName === e.path(siteId)
                    : pathName.startsWith(e.path(siteId))
                })
                if (!isUgcMenuGroup(node)) return toItem(node)
                const children = node.items.map(toItem)
                return {
                  link: children[0].link,
                  name: node.name,
                  selected: children.some((c) => c.selected),
                  childItems: children
                }
              })}
              setPath={setPathName}
              pathName={pathName}
            >
              UGC/レビュー
            </AppNavigationSegment>
          )}
          {site.enable_ai_purchase_assist && canShow('ai_purchase_assist') && (
            <AppNavigationSegment
              color={NavigationColor.RED}
              parent={{
                link: aiPurchaseAssistPath(siteId),
                iconType: IconType.CART,
                subTitle: ModuleIconType.FINDEE
              }}
              items={[
                {
                  link: aiPurchaseAssistPath(siteId),
                  name: 'ダッシュボード',
                  selected: pathName === aiPurchaseAssistPath(siteId)
                },
                {
                  link: aiPurchaseAssistAiProfilesPath(siteId),
                  name: 'AIプロファイル管理',
                  selected: pathName.startsWith(aiPurchaseAssistAiProfilesPath(siteId))
                },
                {
                  link: aiPurchaseAssistOpeningsPath(siteId),
                  name: 'オープニング質問',
                  selected: pathName.startsWith(aiPurchaseAssistOpeningsPath(siteId))
                },
                {
                  link: aiPurchaseAssistRichUIPath(siteId),
                  name: 'リッチUIデザイナー',
                  selected: pathName.startsWith(aiPurchaseAssistRichUIPath(siteId))
                },
                {
                  link: aiPurchaseAssistCampaignsPath(siteId),
                  name: '配信設定',
                  selected: pathName.startsWith(aiPurchaseAssistCampaignsPath(siteId))
                },
                {
                  link: aiPurchaseAssistAbTestsPath(siteId),
                  name: 'A/Bテスト',
                  selected: pathName.startsWith(aiPurchaseAssistAbTestsPath(siteId))
                },
                {
                  link: aiPurchaseAssistConversationsPath(siteId),
                  name: '会話履歴・分析',
                  selected: pathName.startsWith(aiPurchaseAssistConversationsPath(siteId))
                },
                {
                  link: aiPurchaseAssistFeedbackPath(siteId),
                  name: 'フィードバック管理',
                  selected: pathName.startsWith(aiPurchaseAssistFeedbackPath(siteId))
                },
                {
                  link: aiPurchaseAssistKnowledgePath(siteId),
                  name: 'ナレッジ・回答誘導',
                  selected: pathName.startsWith(aiPurchaseAssistKnowledgePath(siteId))
                },
                {
                  link: aiPurchaseAssistNgRulesPath(siteId),
                  name: 'NGワード・禁止トピック',
                  selected: pathName.startsWith(aiPurchaseAssistNgRulesPath(siteId))
                }
              ]}
              setPath={setPathName}
              pathName={pathName}
            >
              AI購入支援
            </AppNavigationSegment>
          )}
          {site.enable_search_suggest && canShow('search_suggest') && (
            <AppNavigationSegment
              color={NavigationColor.RED}
              parent={{
                link: searchSuggestSettingsPath(siteId),
                iconType: IconType.SEARCH_TITLE,
                subTitle: ModuleIconType.FINDEE
              }}
              items={[
                {
                  link: searchSuggestSettingsPath(siteId),
                  name: '検索サジェスト設定',
                  selected: pathName.startsWith(searchSuggestSettingsPath(siteId))
                },
                {
                  link: searchSuggestDictionaryPath(siteId),
                  name: '辞書管理',
                  selected: pathName.startsWith(searchSuggestDictionaryPath(siteId))
                },
                {
                  link: searchSuggestAnalyticsPath(siteId),
                  name: 'ダッシュボード',
                  selected: pathName.startsWith(searchSuggestAnalyticsPath(siteId))
                }
              ]}
              setPath={setPathName}
              pathName={pathName}
            >
              検索サジェスト
            </AppNavigationSegment>
          )}
          {site.enable_tag_pages && canShow('search_tag') && (
            <AppNavigationSegment
              color={NavigationColor.ORANGE}
              parent={{
                link: searchTagDashboardPath(siteId),
                iconType: IconType.HASH,
                subTitle: ModuleIconType.TAG_TRAIL
              }}
              items={[
                {
                  link: searchTagDashboardPath(siteId),
                  name: 'ダッシュボード',
                  selected: pathName.startsWith(searchTagDashboardPath(siteId))
                },
                {
                  link: searchTagListPath(siteId),
                  name: 'タグ一覧',
                  selected: pathName.startsWith(searchTagListPath(siteId))
                },
                {
                  link: searchTagCandidatesPath(siteId),
                  name: '承認キュー',
                  selected: pathName.startsWith(searchTagCandidatesPath(siteId))
                },
                {
                  link: searchTagOptimizationsPath(siteId),
                  name: '最適化提案',
                  selected: pathName.startsWith(searchTagOptimizationsPath(siteId))
                },
                {
                  link: searchTagSettingsPath(siteId),
                  name: 'タグ仕様設定',
                  selected: pathName.startsWith(searchTagSettingsPath(siteId))
                },
                {
                  link: searchTagDeliveryPath(siteId),
                  name: '利用設定（API/配信）',
                  selected: pathName.startsWith(searchTagDeliveryPath(siteId))
                }
              ]}
              setPath={setPathName}
              pathName={pathName}
            >
              ハッシュタグ回遊
            </AppNavigationSegment>
          )}
        </>
      )}
    </Navigation>
  )
}

export default AppNavigation
